import React, { useState, useEffect } from 'react';
import { Button, Input, Upload, Image, message, Spin, Card, Alert, Modal } from 'antd';
import { CameraOutlined, UploadOutlined, CheckCircleOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { getCustomerStorageInventories, getOrderInventoryStatus, createInventoryWithPhoto, uploadInventoryPhoto } from '../../../../data/API';
import { getMyData } from '../../../../utils/token';
import PropTypes from 'prop-types';
import './BoxPhotoUpload.css';

const BoxPhotoUpload = ({ onPhotoUploaded }) => {
  const [loading, setLoading] = useState(true);
  const [storageOrders, setStorageOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [boxNumber, setBoxNumber] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [editImageFile, setEditImageFile] = useState(null);
  const [editPreviewUrl, setEditPreviewUrl] = useState(null);

  useEffect(() => {
    fetchStorageOrders();
  }, []);

  const fetchStorageOrders = async () => {
    try {
      setLoading(true);
      const userData = getMyData();
      const email = userData?.email;

      if (!email) {
        message.error('User email not found. Please log in again.');
        return;
      }

      const response = await getCustomerStorageInventories(email);

      if (response.data && response.data.success) {
        const orders = response.data.data.orders;
        setStorageOrders(orders);

        // Auto-select first order if available
        if (orders.length > 0) {
          handleSelectOrder(orders[0]);
        }
      } else {
        message.error(response.data?.message || 'Failed to fetch storage orders');
      }
    } catch (error) {
      console.error('Error fetching storage orders:', error);
      message.error('Failed to load storage orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrder = async (order) => {
    try {
      setSelectedOrder(order);
      setBoxNumber('');
      setImageFile(null);
      setPreviewUrl(null);

      const userData = getMyData();
      const email = userData?.email;

      const response = await getOrderInventoryStatus(order.id, email);

      if (response.data && response.data.success) {
        setOrderStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching order status:', error);
      message.error('Failed to load order status');
    }
  };

  const handleFileChange = (info) => {
    const file = info.file.originFileObj || info.file;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      message.error('Please select an image file');
      return;
    }

    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setImageFile(null);
    setPreviewUrl(null);
  };

  const handleViewImage = (inventory) => {
    setSelectedInventory(inventory);
    setViewModalVisible(true);
  };

  const handleEditImage = (inventory) => {
    setSelectedInventory(inventory);
    setEditModalVisible(true);
  };

  const handleEditFileChange = (info) => {
    const file = info.file.originFileObj || info.file;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      message.error('Please select an image file');
      return;
    }

    setEditImageFile(file);
    setEditPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveEditImage = () => {
    if (editPreviewUrl) {
      URL.revokeObjectURL(editPreviewUrl);
    }
    setEditImageFile(null);
    setEditPreviewUrl(null);
  };

  const handleUpdatePhoto = async () => {
    if (!editImageFile) {
      message.error('Please select a new photo');
      return;
    }

    try {
      setUploading(true);
      const userData = getMyData();
      const email = userData?.email;

      const response = await uploadInventoryPhoto(
        selectedInventory.id,
        editImageFile,
        email
      );

      if (response.data && response.data.success) {
        message.success('Photo updated successfully!');
        handleRemoveEditImage();
        setEditModalVisible(false);
        setSelectedInventory(null);
        await new Promise(resolve => setTimeout(resolve, 500));

        // Refresh order status to show updated image
        await handleSelectOrder(selectedOrder);

        if (onPhotoUploaded) {
          onPhotoUploaded();
        }
      } else {
        message.error(response.data?.message || 'Failed to update photo');
      }
    } catch (error) {
      console.error('Error updating photo:', error);
      message.error(
        error.response?.data?.message ||
        error.message ||
        'Failed to update photo. Please try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalVisible(false);
    setSelectedInventory(null);
  };

  const handleCloseEditModal = () => {
    handleRemoveEditImage();
    setEditModalVisible(false);
    setSelectedInventory(null);
  };

  const handleUploadPhoto = async () => {
    if (!selectedOrder) {
      message.error('Please select a storage order');
      return;
    }

    if (!boxNumber.trim()) {
      message.error('Please enter a box number');
      return;
    }

    // Validate 7-character alphanumeric format
    const boxNumberPattern = /^[a-zA-Z0-9]{7}$/;
    if (!boxNumberPattern.test(boxNumber.trim())) {
      message.error('Box number must be exactly 7 characters (letters and/or numbers)');
      return;
    }

    if (!imageFile) {
      message.error('Please select a photo');
      return;
    }

    try {
      setUploading(true);
      const userData = getMyData();
      const email = userData?.email;

      const response = await createInventoryWithPhoto(
        selectedOrder.id,
        boxNumber.trim(),
        imageFile,
        email
      );

      if (response.data && response.data.success) {
        message.success(`Photo uploaded successfully for box ${boxNumber}!`);

        // Reset form
        setBoxNumber('');
        handleRemoveImage();
        await new Promise(resolve => setTimeout(resolve, 500));

        // Refresh order status to show the newly uploaded box
        await handleSelectOrder(selectedOrder);
        if (onPhotoUploaded) {
          onPhotoUploaded();
        }
      } else {
        message.error(response.data?.message || 'Failed to upload photo');
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      message.error(
        error.response?.data?.message ||
        error.message ||
        'Failed to upload photo. Please try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="box-upload-loading">
        <Spin size="large" tip="Loading your storage orders..." />
      </div>
    );
  }

  if (storageOrders.length === 0) {
    return (
      <div className="box-upload-empty">
        <Alert
          message="No Storage Orders Found"
          description="You don't have any active storage orders yet. Please place a storage order first."
          type="info"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="box-photo-upload-container">
      <div className="upload-instructions-section">
        <h3>Upload Box Photos</h3>
        <p className="instructions-text">
           Pack your items into your box(es) and take an overhead photo of the contents of the open box before closing lids and sealing with the red security tag.<br></br>
           This will enable you to know what&apos; inside each box when selecting boxes to be delivered. <br></br>
           You&apos;ll also be able to add/edit the contents of each box once photos are uploaded.
        </p>
      </div>

      {/* Order Selection */}
      {storageOrders.length > 1 && (
        <div className="order-selection">
          <h4>Select Storage Order:</h4>
          <div className="order-buttons">
            {storageOrders.map((order) => (
              <Button
                key={order.id}
                type={selectedOrder?.id === order.id ? 'primary' : 'default'}
                onClick={() => handleSelectOrder(order)}
                className="order-select-btn"
              >
                Order #{order.order_number}
                <br></br>
                <small>{order.box_count} boxes</small>
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Order Status */}
      {selectedOrder && orderStatus && (
        <Card className="order-status-card" title={`Order #${selectedOrder.order_number}`}>
          <div className="status-summary">
            <div className="status-item">
              <span className="status-label">Total Boxes</span>
              <span className="status-value">{orderStatus.box_count}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Photos Uploaded</span>
              <span className="status-value success">{orderStatus.uploaded_count}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Remaining</span>
              <span className="status-value warning">{orderStatus.remaining_count}</span>
            </div>
          </div>

          {orderStatus.uploaded_count > 0 && (
            <div className="uploaded-boxes-list">
              <h5>Boxes with Photos:</h5>
              <div className="uploaded-boxes">
                {orderStatus.inventory.map((item) => (
                  <div key={item.id} className="uploaded-box-item">
                    <Image
                      src={item.content_image_url || item.content_image}
                      alt={`Box ${item.box.box_number}`}
                      width={50}
                      height={50}
                      style={{ objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }}
                      preview={false}
                      onClick={() => handleViewImage(item)}
                    />
                    <div className="box-item-info">
                      <CheckCircleOutlined className="check-icon" />
                      <span>Box {item.box.box_number}</span>
                    </div>
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEditImage(item)}
                      className="edit-btn"
                    >
                      Edit
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Upload Form */}
      {selectedOrder && orderStatus && orderStatus.remaining_count > 0 && (
        <Card className="upload-form-card" title="Upload New Box Photo">
          <div className="upload-form">
            <div className="form-group">
              <label>Box Number:</label>
              <Input
                placeholder="Enter 7-character security tag code (e.g., ABC1234)"
                value={boxNumber}
                onChange={(e) => setBoxNumber(e.target.value.toUpperCase())}
                size="large"
                disabled={uploading}
                maxLength={7}
              />
              <small className="help-text">
                Enter the number on the red security tag associated with this box
              </small>
            </div>

            <div className="form-group">
              <label>Box Contents Photo:</label>
              {!previewUrl ? (
                <Upload
                  accept="image/*"
                  capture="environment"
                  showUploadList={false}
                  onChange={handleFileChange}
                  beforeUpload={() => false}
                  maxCount={1}
                  disabled={uploading}
                >
                  <div className="upload-buttons">
                    <Button
                      size="large"
                      icon={<UploadOutlined />}
                      className="gallery-btn"
                      block
                      disabled={uploading}
                    >
                      Choose from Gallery
                    </Button>
                  </div>
                </Upload>
              ) : (
                <div className="photo-preview">
                  <Image
                    src={previewUrl}
                    alt="Box contents preview"
                    className="preview-image"
                    preview={false}
                  />
                  <Button
                    onClick={handleRemoveImage}
                    disabled={uploading}
                    className="remove-photo-btn"
                  >
                    Remove Photo
                  </Button>
                </div>
              )}
            </div>

            <Button
              type="primary"
              size="large"
              onClick={handleUploadPhoto}
              loading={uploading}
              disabled={!boxNumber.trim() || !imageFile}
              className="submit-btn bg-theme-secondary"
              block
            >
              {uploading ? 'Uploading...' : 'Upload Box Photo'}
            </Button>
          </div>
        </Card>
      )}

      {selectedOrder && orderStatus && orderStatus.remaining_count === 0 && (
        <Alert
          message="All Boxes Complete!"
          description={`You've uploaded photos for all ${orderStatus.box_count} boxes in this order. Great job!`}
          type="success"
          showIcon
          className="mt-3"
        />
      )}

      {/* View Image Modal */}
      <Modal
        title={`Box ${selectedInventory?.box?.box_number}`}
        open={viewModalVisible}
        onCancel={handleCloseViewModal}
        footer={[
          <Button key="close" onClick={handleCloseViewModal}>
            Close
          </Button>,
          <Button key="edit" type="primary" onClick={() => {
            handleCloseViewModal();
            handleEditImage(selectedInventory);
          }}>
            Edit Photo
          </Button>
        ]}
        width={700}
        centered
      >
        {selectedInventory && (
          <div style={{ textAlign: 'center' }}>
            <Image
              src={selectedInventory.content_image_url || selectedInventory.content_image}
              alt={`Box ${selectedInventory.box?.box_number}`}
              style={{ maxWidth: '100%' }}
            />
          </div>
        )}
      </Modal>

      {/* Edit Image Modal */}
      <Modal
        title={`Edit Photo for Box ${selectedInventory?.box?.box_number}`}
        open={editModalVisible}
        onCancel={handleCloseEditModal}
        footer={[
          <Button key="cancel" onClick={handleCloseEditModal} disabled={uploading}>
            Cancel
          </Button>,
          <Button
            key="update"
            type="primary"
            onClick={handleUpdatePhoto}
            loading={uploading}
            disabled={!editImageFile}
            className="bg-theme-secondary"
            style={{ color: '#fff' }}
          >
            Update Photo
          </Button>
        ]}
        width={500}
        centered
      >
        <div>
          <p className="mb-2">Upload a new photo for this box</p>
          {!editPreviewUrl ? (
            <Upload
              accept="image/*"
              capture="environment"
              showUploadList={false}
              onChange={handleEditFileChange}
              beforeUpload={() => false}
              maxCount={1}
              disabled={uploading}
            >
              <Button
                size="large"
                icon={<UploadOutlined />}
                block
                disabled={uploading}
              >
                Choose from Gallery
              </Button>
            </Upload>
          ) : (
            <div>
              <Image
                src={editPreviewUrl}
                alt="New photo preview"
                style={{ maxWidth: '100%', marginBottom: '16px' }}
                preview={false}
              />
              <Button
                onClick={handleRemoveEditImage}
                disabled={uploading}
                block
              >
                Remove Photo
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

BoxPhotoUpload.propTypes = {
  onPhotoUploaded: PropTypes.func
};

export default BoxPhotoUpload;
